import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DestroyBase } from '../../../../../components/common/ng-destroy-base';
import { PolluxGridConfig } from '../../../../../components/common/pollux-grid-config/pollux-grid-config';
import { PublishService } from '../../../services/publish.service';
import { reportPropertiesGridColumns } from './app-publish-dialog-grid-config';
import { State } from '@progress/kendo-data-query';
import { EventBusService } from '../../../../../services/event-bus.service';
import { Observable, forkJoin } from 'rxjs';

/**
 * Publish (print) dialog
 */
@Component({
  selector: 'app-publish-dialog',
  templateUrl: './app-publish-dialog.component.html',
  styleUrls: ['./app-publish-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppPublishDialogComponent extends DestroyBase implements OnInit {
  /**
  * Is dialog visible flag.
  */
  public isDialogVisible = false;

  /**
  * Selected portfolio id.
  */
  portfolioId: number;

  /**
  * Active publish method.
  */
  activePublishMethod: string;

  /**
  * Mode.
  */
  mode: string;

  /**
  * State.
  */
  state: string;

  /**
  * Reports.
  */
  reportsSelection = [];

  /**
  * Merge.
  */
  merge = true;

  /**
  * Flag for all checkboxes.
  */
  checkAllChecked = false;

  /**
  * Dialog title.
  */
  windowTitle = 'Publish';

  /**
  * Data source for grid.
  */
  selectableReportsDataSource = {
    data: [],
    page: 1,
    pageSize: 25
  };

  /**
  * Settings for print.
  */
  settings = {
    addWatermark: false,
    hideBackCoverPage: true,
    hideCoverPage: true,
    hidePageNumbers: false,
    imageQuality: 70,
    includeFooter: true,
    includeHeader: true,
    markAsFinal: false,
    passwordProtect: false,
    termsAndConditions: false,
    useCompression: true,
    watermarkText: 'DRAFT',
  };

  /**
  * Publish methods.
  */
  publishMethods = [
    {
      description: 'PDF',
      value: '1', //PDF
      enabled: true
    },
    {
      description: 'HTML',
      value: '6', //static HTML
      enabled: true,
      comingSoon: false
    }
  ];

  /**
  * Pager config
  */
  protected pagerConfig: any = {
    pageSize: 50,
    sizes: [10, 25, 50, 100],
    numericButtonsCount: 0, //how many buttons for pages
  }

  /**
  * The current grid state
  */
  public gridState: State = {
    skip: 0,
    take: this.pagerConfig.pageSize,
    group: [],
    filter: { filters: [], logic: "and" },
    sort: []
  };

  /**
   * Constructor
   * @param eventBus
   * @param publishService
   */
  constructor(private eventBus: EventBusService, private publishService: PublishService) {
    super();
    this.checkAllChecked = false;
  }

  /**
  *OnInit
  */
  ngOnInit() {
    this.subscribeToSelectPublishMethod();
    this.subscribeToSelectPublishPortfolioMethod();
    this.watchIndividualCheckboxes();
  }

  /**
  * Subscribes to event to select publish method
  */
  subscribeToSelectPublishMethod() {
    this.onEvents.push(this.eventBus.subscribeEvent('selectPublishReportMethod', (items) => {
      this.reset();
      this.mode = 'report';
      this.isDialogVisible = true;
      this.reportsSelection = items;
    }));
  }

  /**
  * Subscribes to event to select publish portfolio method
  */
  subscribeToSelectPublishPortfolioMethod() {
    this.onEvents.push(this.eventBus.subscribeEvent('selectPublishPortfolioMethod', (item) => {
      this.reset();
      this.mode = 'portfolio';
      this.isDialogVisible = true;
      this.portfolioId = item;
      this.state = 'start';
    }));
  }

  /**
  * Reset data
  */
  reset() {
    this.state = 'start';
    this.mode = 'report';
    this.portfolioId = -1;
    this.activePublishMethod = '1';
    this.reportsSelection = [];
    this.selectableReportsDataSource.data = [];
    this.settings = {
      addWatermark: false,
      hideBackCoverPage: true,
      hideCoverPage: true,
      hidePageNumbers: false,
      imageQuality: 70,
      includeFooter: true,
      includeHeader: true,
      markAsFinal: false,
      passwordProtect: false,
      termsAndConditions: false,
      useCompression: true,
      watermarkText: 'DRAFT',
    };

  }

  /**
   * Cancel event for dialog
   */
  cancel() {
    this.isDialogVisible = false;
    this.reset();
  }

  /**
  * Changes state to 'start'
  */
  back() {
    this.state = 'start';
  }

  /**
  * Confirm event for publish
  */
  confirm() {
    if (this.mode === 'report') {
      this.enqueueReportPrintRequest();
    } else if (this.state === 'start' && this.mode !== 'report') {
      this.selectProperties();
    } else if (this.mode != 'report') {
      this.enqueuePortfolioPrintRequest();
    }
  }

  /**
   * Enqueue a portfolio print request (multiple reports)
   * @returns
   */
  private enqueuePortfolioPrintRequest() {
    const reportIds = [];
    const missingConversionRates = [];
    this.selectableReportsDataSource.data.forEach(element => {
      if (element.checkbox) {
        if (!element.HasCurrencyConversionRate) {
          missingConversionRates.push(element);
        }
        reportIds.push(element.Oid);
      }
    });

    if (missingConversionRates.length > 0) {
      this.handleMissingConversionRate(missingConversionRates);
      this.closeDialog();
      return;
    }

    this.state = 'enqueuing';
    const request = {
      printMethod: this.activePublishMethod,
      reportIds: reportIds,
      portfolioId: this.portfolioId,
      merge: this.merge,
      settings: this.settings
    };

    this.publishService.printPublishPortfolio(request).subscribe({
      next: (data) => {
        if (data?.success) {
          this.state = 'enqueued';
          this.isDialogVisible = false;
          this.eventBus.broadcastEvent('valuationPortfolioLoadDataFromPublish');
          this.eventBus.broadcastEvent('reportPrintLoadData');
        } else {
          this.state = 'start';
        }
      },
      error: (error) => {
        this.state = 'start';
        this.handleReportNotInLandscape(error);
        this.closeDialog();
      }
    });
  }

  /**
   * Select properties
   */
  private selectProperties() {
    this.state = "selectProperties";
    if (this.selectableReportsDataSource.data.length == 0) {
      this.publishService.reportFilter(this.portfolioId).subscribe(data => {
        data = data.filter(item => data.RecordStatusId !== 2);
        this.selectableReportsDataSource.data = data;
      });
    }
  }

  /**
   * Enqueues a report print request
   */
  private enqueueReportPrintRequest() {
    this.state = "enqueuing";
    const requests: Observable<any>[] = [];
    for (const report of this.reportsSelection) {
      const request = {
        printMethod: this.activePublishMethod,
        reportIds: [report.Oid],
        settings: this.settings
      };

      requests.push(this.publishService.printPublishReport(request));
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.state = 'enqueued';
        this.closeDialog();
        this.eventBus.broadcastEvent('reportPrintLoadData');
      },
      error: (error) => {
        this.handleAtLeastOneActiveReportError(error);
        this.handleReportNotInLandscape(error);
        this.closeDialog();
      }
    });
  }

  /**
  * Get columns config
  */
  public get columns(): PolluxGridConfig[] {
    return reportPropertiesGridColumns;
  }

  /**
  * Close dialog
  */
  closeDialog() {
    this.isDialogVisible = false;
  }

  /**
  * Handles error for at least on active report case
  */
  handleAtLeastOneActiveReportError(error: any) {
    if (JSON.stringify(error).indexOf('at least one report has to be active') >= 0) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'atLeastOneActiveReport');
    }
  }

  /**
  * Handles error for at report not in landscape case
  */
  handleReportNotInLandscape(error: any) {
    if (JSON.stringify(error).indexOf('at least one report is not in landscape format') >= 0) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'reportNotInLandscape');
    }
  }

  /**
  * Handles error for missing conversion rate case
  */
  handleMissingConversionRate(missingConversionRates: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      customMessage: () => {
        let message = `Please enter an exchange rate for the currency pair(s): <br>`;
        missingConversionRates.forEach(report => message += `${report.LocalCurrencyDescription} - ${report.ReportCurrencyDescription} <br>`);
        message += `for this Valuation Event.`
        return message;
      },
      success: () => {
        //Nothing
      }
    }, 'missingConversionRate');
  }

  /**
  * Opens dialog for report not in landscape
  */
  handleReportNotInLandscapeDialog() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      }
    }, 'reportNotInLandscape');
  }

  /**
  * Flag for check/uncheck all checkboxes
  */
  selectOrDeselectAllCheckbox() {
    for (const property of this.selectableReportsDataSource.data) {
      property.checkbox = this.checkAllChecked;
    }
  }

  /**
   * Match individual checboxes for the check all checkbox
   * @returns
   */
  watchIndividualCheckboxes() {
    for (const property of this.selectableReportsDataSource.data) {
      if (!property.checkbox) {
        this.checkAllChecked = false;
        return;
      }
    }
    this.checkAllChecked = true;
  }

  /**
   * Gets the select all state
   */
  selectOrDeselectCheckbox() {
    this.checkAllChecked = !this.selectableReportsDataSource.data.find(property => !property.checkbox);
  }

}
